import { ErrorBoundary } from '@sentry/react'
import React, { Suspense } from 'react'
import Skeleton from 'react-loading-skeleton'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useRecommendedPartnerModal } from '@/hooks/useRecommendedPartnerModal'
import { cn } from '@/lib/utils'
import RecommendedPartnerModal from '@/pages/professionalNetworkRelationships/components/RecommendedPartnerModal/RecommendedPartnerModal'
import { RECOMMENDED_PARTNER_VIDEO_HEIGHT } from '@/pages/professionalNetworkRelationships/constants'

const RecommendedPartnerModalContainer: React.FC = () => {
  const recommendedPartnerModal = useRecommendedPartnerModal()

  if (!recommendedPartnerModal.params.recommendedPartnerId) {
    return null
  }

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          recommendedPartnerModal.hide()
        }
      }}
    >
      <DialogContent
        showCloseButton={false}
        size="xl"
        className={cn(
          'RecommendedPartnerModal',
          `RecommendedPartnerModal__${recommendedPartnerModal.params.recommendedPartnerStep}`,
        )}
      >
        <ErrorBoundary fallback={<ErrorFallback />}>
          <Suspense fallback={<LoadingSkeleton />}>
            <RecommendedPartnerModal />
          </Suspense>
        </ErrorBoundary>
      </DialogContent>
    </Dialog>
  )
}

const LoadingSkeleton: React.FC = () => {
  const recommendedPartnerModal = useRecommendedPartnerModal()

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          recommendedPartnerModal.hide()
        }
      }}
    >
      <DialogContent size="xl" showCloseButton={false}>
        <Skeleton style={{ height: RECOMMENDED_PARTNER_VIDEO_HEIGHT }} />
      </DialogContent>
    </Dialog>
  )
}

const ErrorFallback: React.FC = () => {
  const recommendedPartnerModal = useRecommendedPartnerModal()

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          recommendedPartnerModal.hide()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unable to fetch details</DialogTitle>
        </DialogHeader>

        <p className="mb-4">
          Oops, it looks there was a problem loading the details for this vetted
          partner.
        </p>

        <p className="mb-0">
          Our team has been notified. Please try again later.
        </p>
      </DialogContent>

      <DialogFooter>
        <Button
          variant="outline-secondary"
          onClick={recommendedPartnerModal.hide}
        >
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default RecommendedPartnerModalContainer
